import React, { createContext, useContext, useState, useMemo } from 'react'
import {
  Bell,
  RefreshCw,
  CheckCircle2,
  TrendingUp,
  ArrowLeftRight,
  AlertCircle,
  AlertTriangle,
  Info,
  Cpu,
} from 'lucide-react'

// --- Types ---

export type NotificationType =
  | 'data_availability'
  | 'threshold'
  | 'correction'
  | 'model_divergence'
  | 'system'
export type EventType = 'triggered' | 'changed' | 'recovered' | null
export type UICategory =
  | 'Forecast update'
  | 'Market signal'
  | 'Model revision'
  | 'System'
export type Severity =
  | 'critical'
  | 'warning'
  | 'info'
  | 'model_update'
  | 'market'

export interface NotificationCondition {
  variable_display_name: string
  operator: string
  operator_phrase: string
  threshold: number
  unit: string
}

export interface ModelRow {
  display_name: string
  init_time?: string
  init_time_formatted?: string
  valid_time?: string
  valid_time_formatted?: string
  values?: (number | null)[]
  // correction-specific:
  old_values?: (number | null)[]
  new_values?: (number | null)[]
  abs_change?: (number | null)[]
  pct_change?: (number | null)[]
  previous_run_time?: string
  current_run_time?: string
}

export interface TriggeredLocation {
  location_label: string
  models: ModelRow[]
}

export interface NotificationDetails {
  mode?: 'any' | 'all'
  min_forecast_minutes?: number
  triggered_models?: { name: string; display_name: string }[]
  conditions?: NotificationCondition[]
  triggered_locations?: TriggeredLocation[]
  message?: string
}

export interface AppNotification {
  id: string
  alert_name: string
  notification_type: NotificationType
  event_type: EventType
  severity: Severity
  sort_date: string // ISO
  workspace_natural_id?: string
  workspace_name?: string
  link_url?: string
  has_state_details: boolean
  is_superseded: boolean
  stale_reason?: 'alert_removed' | 'superseded' | null
  signature_changed: boolean
  details: NotificationDetails
  read: boolean
}

// --- Helpers ---

export const getUICategory = (type: NotificationType): UICategory => {
  switch (type) {
    case 'data_availability':
      return 'Forecast update'
    case 'threshold':
      return 'Market signal'
    case 'correction':
      return 'Model revision'
    case 'model_divergence':
      return 'Model revision'
    case 'system':
      return 'System'
  }
}

export const getIcon = (
  type: NotificationType,
  details: NotificationDetails,
  severity?: Severity,
) => {
  if (severity) {
    switch (severity) {
      case 'critical':
        return AlertCircle
      case 'warning':
        return AlertTriangle
      case 'model_update':
        return Cpu
      case 'market':
        return TrendingUp
      case 'info':
        return Info
    }
  }
  switch (type) {
    case 'data_availability':
      return details.mode === 'all' ? CheckCircle2 : RefreshCw
    case 'threshold':
      return TrendingUp
    case 'correction':
      return ArrowLeftRight
    case 'model_divergence':
      return Cpu
    case 'system':
      return Bell
  }
}

export const getBadgeLabel = (
  eventType: EventType,
  notificationType: NotificationType,
): string => {
  if (eventType === 'triggered') return 'Triggered'
  if (eventType === 'changed') return 'Updated'
  if (eventType === 'recovered') return 'Recovered'
  return getUICategory(notificationType)
}

export const getBadgeStyle = (
  eventType: EventType,
): { bg: string; text: string; border: string } => {
  switch (eventType) {
    case 'triggered':
      return {
        bg: 'bg-red-500/15',
        text: 'text-red-400',
        border: 'border-red-500/25',
      }
    case 'changed':
      return {
        bg: 'bg-amber-500/15',
        text: 'text-amber-400',
        border: 'border-amber-500/25',
      }
    case 'recovered':
      return {
        bg: 'bg-emerald-500/15',
        text: 'text-emerald-400',
        border: 'border-emerald-500/25',
      }
    default:
      return {
        bg: 'bg-slate-700/50',
        text: 'text-slate-400',
        border: 'border-slate-600/50',
      }
  }
}

export const getIconStyle = (
  type: NotificationType,
  severity?: Severity,
): { bg: string; color: string; border: string } => {
  // Glass style — professional, muted, frosted
  // Original colorful styles preserved below as comments for revert:
  //
  // critical:  { bg: 'bg-red-500/10', color: 'text-red-400', border: 'border-red-500/20' }
  // warning:   { bg: 'bg-amber-500/10', color: 'text-amber-400', border: 'border-amber-500/20' }
  // model_update: { bg: 'bg-purple-500/10', color: 'text-purple-400', border: 'border-purple-500/20' }
  // market:    { bg: 'bg-emerald-500/10', color: 'text-emerald-400', border: 'border-emerald-500/20' }
  // info:      { bg: 'bg-blue-500/10', color: 'text-blue-400', border: 'border-blue-500/20' }
  // data_availability: { bg: 'bg-blue-500/10', color: 'text-blue-400', border: 'border-blue-500/20' }
  // threshold: { bg: 'bg-red-500/10', color: 'text-red-400', border: 'border-red-500/20' }
  // correction: { bg: 'bg-purple-500/10', color: 'text-purple-400', border: 'border-purple-500/20' }
  // system:    { bg: 'bg-slate-700/30', color: 'text-slate-400', border: 'border-slate-600/30' }

  return {
    bg: 'bg-white/[0.06]',
    color: 'text-white/70',
    border: 'border-white/[0.08]',
  }
}

export const generateMeaningLine = (n: AppNotification): string => {
  const { details, notification_type, event_type } = n

  // 10.2 Data availability
  if (notification_type === 'data_availability') {
    if (event_type === 'triggered') {
      if (details.mode === 'all') {
        const hours = details.min_forecast_minutes
          ? Math.round(details.min_forecast_minutes / 60)
          : 0
        return `New forecast run available. Coverage complete through +${hours}h.`
      }
      return 'New forecast run available. Partial model coverage.'
    }
    if (event_type === 'changed') return 'Forecast data updated for this alert.'
    if (event_type === 'recovered')
      return 'Data availability recovered for this alert.'
    return 'Forecast update available for this alert.'
  }

  // 10.3 Threshold
  if (notification_type === 'threshold') {
    if (details.conditions && details.conditions.length > 0) {
      const cond = details.conditions[0]
      const locCount = details.triggered_locations?.length || 0
      const locPhrase =
        locCount > 0 ? ` at ${locCount} location${locCount > 1 ? 's' : ''}` : ''

      // Find valid time from first model of first location if available
      let timePhrase = ''
      if (details.triggered_locations?.[0]?.models?.[0]?.valid_time_formatted) {
        const vt = details.triggered_locations[0].models[0].valid_time_formatted
        // Make the time context clear based on the format
        if (vt === 'Now') {
          timePhrase = ' (current)'
        } else if (vt.includes('D+') || vt === 'Tomorrow' || vt === 'Today') {
          timePhrase = ` (${vt})`
        } else {
          timePhrase = ` (valid ${vt})`
        }
      }

      return `${cond.variable_display_name} ${cond.operator_phrase} ${cond.threshold}${cond.unit}${locPhrase}${timePhrase}.`
    }

    // Fallbacks
    if (event_type === 'triggered') return 'Threshold condition triggered.'
    if (event_type === 'changed') return 'Threshold condition updated.'
    if (event_type === 'recovered') return 'Threshold conditions no longer met.'
    return 'Threshold notification.'
  }

  // 10.4 Correction
  if (notification_type === 'correction') {
    if (details.conditions && details.conditions.length > 0) {
      const cond = details.conditions[0]

      // Try to find delta summary from first location/model
      let deltaSummary = 'changed vs previous run'
      const firstModel = details.triggered_locations?.[0]?.models?.[0]
      if (firstModel) {
        if (
          firstModel.pct_change?.[0] !== undefined &&
          firstModel.pct_change[0] !== null
        ) {
          const val = firstModel.pct_change[0]
          deltaSummary = `${val > 0 ? '+' : ''}${val}%`
        } else if (
          firstModel.abs_change?.[0] !== undefined &&
          firstModel.abs_change[0] !== null
        ) {
          const val = firstModel.abs_change[0]
          deltaSummary = `${val > 0 ? '+' : ''}${val}${cond.unit}`
        }
      }

      let timePhrase = ''
      if (firstModel?.valid_time_formatted) {
        const vt = firstModel.valid_time_formatted
        if (vt === 'Now') {
          timePhrase = ' (current)'
        } else if (vt.includes('D+') || vt === 'Tomorrow' || vt === 'Today') {
          timePhrase = ` (${vt})`
        } else {
          timePhrase = ` (valid ${vt})`
        }
      }

      return `Forecast revised: ${cond.variable_display_name} ${deltaSummary}${timePhrase}.`
    }

    // Fallbacks
    if (event_type === 'triggered') return 'Forecast correction detected.'
    if (event_type === 'changed') return 'Forecast correction updated.'
    if (event_type === 'recovered') return 'No more forecast corrections.'
    return 'Correction notification.'
  }

  // 10.5 Model Divergence
  if (notification_type === 'model_divergence') {
    if (details.conditions && details.conditions.length > 0) {
      const cond = details.conditions[0]
      const locCount = details.triggered_locations?.length || 0
      const locPhrase =
        locCount > 0 ? ` at ${locCount} location${locCount > 1 ? 's' : ''}` : ''
      return `Models disagree on ${cond.variable_display_name}: spread exceeds ${cond.threshold}${cond.unit}${locPhrase}.`
    }
    if (event_type === 'triggered')
      return 'Significant model divergence detected.'
    if (event_type === 'recovered') return 'Model divergence has resolved.'
    return 'Model divergence notification.'
  }

  // 10.6 Generic/system
  return details.message || 'Notification received.'
}

export const getStaleReasonCopy = (
  reason?: 'alert_removed' | 'superseded' | null,
): string => {
  if (reason === 'alert_removed') return 'This alert has been deleted.'
  if (reason === 'superseded')
    return 'This is an older notification. See the latest for current details.'
  return 'Notification details are no longer available.'
}

// --- Mock Data ---

const MOCK_NOTIFICATIONS: AppNotification[] = [
  {
    id: '1',
    alert_name: 'Significant Wind Deviation Alert',
    notification_type: 'threshold',
    event_type: 'triggered',
    severity: 'critical',
    sort_date: new Date(Date.now() - 2 * 60000).toISOString(),
    workspace_name: 'Short Term Trader',
    has_state_details: true,
    is_superseded: false,
    signature_changed: false,
    read: false,
    details: {
      conditions: [
        {
          variable_display_name: 'Wind Speed 100m',
          operator: '<',
          operator_phrase: 'below',
          threshold: 4,
          unit: 'm/s',
        },
      ],
      triggered_locations: [
        {
          location_label: 'Mitte',
          models: [
            {
              display_name: 'EPT-2',
              valid_time_formatted: '14:00',
              values: [3.8],
            },
            {
              display_name: 'EC IFS',
              valid_time_formatted: '14:00',
              values: [3.9],
            },
          ],
        },
        {
          location_label: 'Hamburg',
          models: [
            {
              display_name: 'EPT-2',
              valid_time_formatted: '14:00',
              values: [3.5],
            },
          ],
        },
      ],
    },
  },
  {
    id: '2',
    alert_name: 'EPT-2 Model Run Available',
    notification_type: 'data_availability',
    event_type: 'triggered',
    severity: 'model_update',
    sort_date: new Date(Date.now() - 5 * 60000).toISOString(),
    has_state_details: true,
    is_superseded: false,
    signature_changed: false,
    read: false,
    details: {
      mode: 'all',
      min_forecast_minutes: 10080,
      triggered_models: [
        { name: 'EPT-2', display_name: 'EPT-2' },
        { name: 'EC AIFS', display_name: 'EC AIFS' },
        { name: 'EC IFS', display_name: 'EC IFS' },
      ],
    },
  },
  {
    id: '3',
    alert_name: 'Model Divergence: Nordic Wind D+1',
    notification_type: 'threshold',
    event_type: 'triggered',
    severity: 'warning',
    sort_date: new Date(Date.now() - 12 * 60000).toISOString(),
    workspace_name: 'Meteorologist',
    has_state_details: true,
    is_superseded: false,
    signature_changed: false,
    read: false,
    details: {
      conditions: [
        {
          variable_display_name: 'Model Spread',
          operator: '>',
          operator_phrase: 'divergence >',
          threshold: 15,
          unit: '%',
        },
      ],
      triggered_locations: [
        {
          location_label: 'SE3',
          models: [
            {
              display_name: 'EPT-2 vs EC IFS',
              valid_time_formatted: 'D+1',
              values: [18.5],
            },
          ],
        },
      ],
    },
  },
  {
    id: '4',
    alert_name: 'NL Spot Price Spike €187/MWh',
    notification_type: 'threshold',
    event_type: 'triggered',
    severity: 'market',
    sort_date: new Date(Date.now() - 18 * 60000).toISOString(),
    workspace_name: 'Energy Trader',
    has_state_details: true,
    is_superseded: false,
    signature_changed: false,
    read: false,
    details: {
      conditions: [
        {
          variable_display_name: 'Spot Price',
          operator: '>',
          operator_phrase: 'above',
          threshold: 150,
          unit: '€/MWh',
        },
      ],
      triggered_locations: [
        {
          location_label: 'Netherlands',
          models: [
            {
              display_name: 'EPEX Spot',
              valid_time_formatted: '18:00',
              values: [187.5],
            },
          ],
        },
      ],
    },
  },
  {
    id: '5',
    alert_name: 'French Nuclear Update: +1.2GW',
    notification_type: 'system',
    event_type: null,
    severity: 'info',
    sort_date: new Date(Date.now() - 25 * 60000).toISOString(),
    has_state_details: false,
    is_superseded: false,
    signature_changed: false,
    read: true,
    details: {
      message:
        'French nuclear availability updated: +1.2GW. Revise FR DA position accordingly.',
    },
  },
  {
    id: '6',
    alert_name: 'DE-FR Interconnector NTC Reduced',
    notification_type: 'threshold',
    event_type: 'changed',
    severity: 'warning',
    sort_date: new Date(Date.now() - 32 * 60000).toISOString(),
    workspace_name: 'Energy Trader',
    has_state_details: true,
    is_superseded: false,
    signature_changed: false,
    read: true,
    details: {
      conditions: [
        {
          variable_display_name: 'NTC Capacity',
          operator: '<',
          operator_phrase: 'below',
          threshold: 2500,
          unit: 'MW',
        },
      ],
      triggered_locations: [
        {
          location_label: 'DE -> FR',
          models: [
            {
              display_name: 'JAO',
              valid_time_formatted: 'Tomorrow',
              values: [2100],
            },
          ],
        },
      ],
    },
  },
  {
    id: '7',
    alert_name: 'EC AIFS 12z Run Complete',
    notification_type: 'data_availability',
    event_type: 'triggered',
    severity: 'model_update',
    sort_date: new Date(Date.now() - 45 * 60000).toISOString(),
    has_state_details: true,
    is_superseded: false,
    signature_changed: false,
    read: true,
    details: {
      mode: 'all',
      min_forecast_minutes: 14400,
      triggered_models: [{ name: 'EC AIFS', display_name: 'EC AIFS' }],
    },
  },
  {
    id: '8',
    alert_name: 'Solar Forecast Revised: Southern Italy -8%',
    notification_type: 'correction',
    event_type: 'triggered',
    severity: 'critical',
    sort_date: new Date(Date.now() - 60 * 60000).toISOString(),
    workspace_name: 'Meteorologist',
    has_state_details: true,
    is_superseded: false,
    signature_changed: false,
    read: true,
    details: {
      conditions: [
        {
          variable_display_name: 'Solar CF',
          operator: 'change',
          operator_phrase: 'change',
          threshold: 5,
          unit: '%',
        },
      ],
      triggered_locations: [
        {
          location_label: 'Southern Italy',
          models: [
            {
              display_name: 'EPT-2',
              valid_time_formatted: 'D+1',
              old_values: [24.5],
              new_values: [16.5],
              pct_change: [-8.0],
            },
          ],
        },
      ],
    },
  },
  {
    id: '9',
    alert_name: 'Balancing Market: aFRR Activated 320MW',
    notification_type: 'threshold',
    event_type: 'triggered',
    severity: 'market',
    sort_date: new Date(Date.now() - 65 * 60000).toISOString(),
    workspace_name: 'Short Term Trader',
    has_state_details: true,
    is_superseded: false,
    signature_changed: false,
    read: true,
    details: {
      conditions: [
        {
          variable_display_name: 'aFRR Activation',
          operator: '>',
          operator_phrase: 'exceeds',
          threshold: 300,
          unit: 'MW',
        },
      ],
      triggered_locations: [
        {
          location_label: 'Belgium',
          models: [
            {
              display_name: 'Elia Realtime',
              valid_time_formatted: 'Now',
              values: [320],
            },
          ],
        },
      ],
    },
  },
  {
    id: '10',
    alert_name: 'GFS 06z Shows Wind Downgrade D+2',
    notification_type: 'correction',
    event_type: 'triggered',
    severity: 'model_update',
    sort_date: new Date(Date.now() - 120 * 60000).toISOString(),
    workspace_name: 'Energy Analyst',
    has_state_details: true,
    is_superseded: false,
    signature_changed: false,
    read: true,
    details: {
      conditions: [
        {
          variable_display_name: 'Wind Generation',
          operator: 'change',
          operator_phrase: 'change',
          threshold: 1,
          unit: 'GW',
        },
      ],
      triggered_locations: [
        {
          location_label: 'Central Europe',
          models: [
            {
              display_name: 'GFS',
              valid_time_formatted: 'D+2',
              old_values: [14.2],
              new_values: [11.8],
              abs_change: [-2.4],
            },
          ],
        },
      ],
    },
  },
  {
    id: '11',
    alert_name: 'Borssele Offshore Curtailment Lifted',
    notification_type: 'system',
    event_type: null,
    severity: 'info',
    sort_date: new Date(Date.now() - 130 * 60000).toISOString(),
    has_state_details: false,
    is_superseded: false,
    signature_changed: false,
    read: true,
    details: {
      message:
        'Tennet reports Borssele offshore cluster curtailment lifted. 1,500MW wind back online.',
    },
  },
  {
    id: '12',
    alert_name: 'EPEX DA Cleared DE at €94.20/MWh',
    notification_type: 'threshold',
    event_type: 'triggered',
    severity: 'market',
    sort_date: new Date(Date.now() - 180 * 60000).toISOString(),
    workspace_name: 'Energy Trader',
    has_state_details: true,
    is_superseded: false,
    signature_changed: false,
    read: true,
    details: {
      conditions: [
        {
          variable_display_name: 'Clearing Price',
          operator: '>',
          operator_phrase: 'above',
          threshold: 90,
          unit: '€/MWh',
        },
      ],
      triggered_locations: [
        {
          location_label: 'Germany',
          models: [
            {
              display_name: 'EPEX Spot',
              valid_time_formatted: 'D+1',
              values: [94.2],
            },
          ],
        },
      ],
    },
  },
  {
    id: '13',
    alert_name: 'Flamanville 3 EPR Unplanned Outage',
    notification_type: 'threshold',
    event_type: 'triggered',
    severity: 'critical',
    sort_date: new Date(Date.now() - 190 * 60000).toISOString(),
    workspace_name: 'Short Term Trader',
    has_state_details: true,
    is_superseded: false,
    signature_changed: false,
    read: true,
    details: {
      conditions: [
        {
          variable_display_name: 'Capacity Offline',
          operator: '>',
          operator_phrase: 'exceeds',
          threshold: 1000,
          unit: 'MW',
        },
      ],
      triggered_locations: [
        {
          location_label: 'Flamanville 3',
          models: [
            {
              display_name: 'REMIT',
              valid_time_formatted: 'Now',
              values: [1650],
            },
          ],
        },
      ],
    },
  },
  {
    id: '14',
    alert_name: 'UK Demand Below Seasonal Normal',
    notification_type: 'correction',
    event_type: 'triggered',
    severity: 'info',
    sort_date: new Date(Date.now() - 240 * 60000).toISOString(),
    workspace_name: 'Energy Analyst',
    has_state_details: true,
    is_superseded: true,
    stale_reason: 'superseded',
    signature_changed: false,
    read: true,
    details: {
      conditions: [
        {
          variable_display_name: 'Demand',
          operator: 'change',
          operator_phrase: 'deviation',
          threshold: 5,
          unit: '%',
        },
      ],
      triggered_locations: [
        {
          location_label: 'UK',
          models: [
            {
              display_name: 'National Grid',
              valid_time_formatted: 'Today',
              old_values: [32.5],
              new_values: [30.8],
              pct_change: [-5.2],
            },
          ],
        },
      ],
    },
  },
  {
    id: '15',
    alert_name: 'NO2-DE Flow at 94% NTC Utilisation',
    notification_type: 'threshold',
    event_type: 'triggered',
    severity: 'market',
    sort_date: new Date(Date.now() - 300 * 60000).toISOString(),
    workspace_name: 'Energy Trader',
    has_state_details: true,
    is_superseded: false,
    signature_changed: true,
    read: true,
    details: {
      conditions: [
        {
          variable_display_name: 'NTC Utilisation',
          operator: '>',
          operator_phrase: 'above',
          threshold: 90,
          unit: '%',
        },
      ],
      triggered_locations: [
        {
          location_label: 'NO2 -> DE',
          models: [
            {
              display_name: 'Nord Pool',
              valid_time_formatted: 'Now',
              values: [94.0],
            },
          ],
        },
      ],
    },
  },
]

// --- Context ---

interface NotificationsContextType {
  notifications: AppNotification[]
  unreadCount: number
  markAsRead: (id: string) => void
  markAllRead: () => void
  dismissNotification: (id: string) => void
  toggleRead: (id: string) => void
  getFilteredNotifications: (
    filter: 'all' | 'unread' | 'critical' | 'warning' | 'info',
  ) => AppNotification[]
}

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined)

export function NotificationsProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [notifications, setNotifications] =
    useState<AppNotification[]>(MOCK_NOTIFICATIONS)

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications],
  )

  const markAsRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    )
  }

  const markAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
  }

  const dismissNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }

  const toggleRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: !n.read } : n)),
    )
  }

  const getFilteredNotifications = (
    filter: 'all' | 'unread' | 'critical' | 'warning' | 'info',
  ) => {
    return notifications.filter((n) => {
      if (filter === 'all') return true
      if (filter === 'unread') return !n.read
      return true
    })
  }

  const value = {
    notifications,
    unreadCount,
    markAsRead,
    markAllRead,
    dismissNotification,
    toggleRead,
    getFilteredNotifications,
  }

  return React.createElement(NotificationsContext.Provider, { value }, children)
}

export function useNotifications() {
  const context = useContext(NotificationsContext)
  if (context === undefined) {
    throw new Error(
      'useNotifications must be used within a NotificationsProvider',
    )
  }
  return context
}
